import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchParkings } from '../store/parkingSlice';

// Fix Leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const createParkingIcon = (available, total) => {
  const pct = total > 0 ? available / total : 0;
  const color = pct > 0.5 ? '#10b981' : pct > 0.2 ? '#f59e0b' : '#ef4444';
  return L.divIcon({
    className: '',
    html: `<div style="
      background: ${color}; color: white; border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg); width: 36px; height: 36px;
      display: flex; align-items: center; justify-content: center;
      border: 3px solid white; box-shadow: 0 4px 12px rgba(0,0,0,0.4);
      font-size: 14px; font-weight: 800;
    "><span style="transform: rotate(45deg)">P</span></div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 36],
    popupAnchor: [0, -36],
  });
};

const userIcon = L.divIcon({
  className: '',
  html: `<div style="
    background: #6366f1; border-radius: 50%; width: 20px; height: 20px;
    border: 3px solid white; box-shadow: 0 0 0 6px rgba(99,102,241,0.3);
    animation: pulse 2s infinite;
  "></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

function MapController({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, 14);
  }, [center, map]);
  return null;
}

// CSN center coordinates
const CSN_CENTER = [19.8762, 75.3433];

export default function MapPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { parkings, loading } = useSelector(s => s.parking);
  const [userLocation, setUserLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState(CSN_CENTER);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [filter, setFilter] = useState({ vehicleType: '', maxPrice: '', amenity: '' });
  const [selectedParking, setSelectedParking] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    dispatch(fetchParkings());
  }, [dispatch]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const loc = [pos.coords.latitude, pos.coords.longitude];
          setUserLocation(loc);
          // Only center on user if they're near CSN
          const dist = Math.abs(loc[0] - CSN_CENTER[0]) + Math.abs(loc[1] - CSN_CENTER[1]);
          if (dist < 1) setMapCenter(loc);
        },
        () => setMapCenter(CSN_CENTER)
      );
    }
  }, []);

  const filteredParkings = parkings.filter(p => {
    const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.address.toLowerCase().includes(search.toLowerCase());
    const matchVehicle = !filter.vehicleType || (p.vehicleTypes || []).includes(filter.vehicleType);
    const matchPrice = !filter.maxPrice || p.pricePerHour <= parseInt(filter.maxPrice);
    const matchAmenity = !filter.amenity || (p.amenities || []).includes(filter.amenity);
    return matchSearch && matchVehicle && matchPrice && matchAmenity;
  });

  const availPct = (p) => p.totalSlots > 0 ? (p.availableSlots / p.totalSlots) * 100 : 0;
  const statusColor = (p) => availPct(p) > 50 ? '#10b981' : availPct(p) > 20 ? '#f59e0b' : '#ef4444';
  const statusText = (p) => availPct(p) > 50 ? 'Available' : availPct(p) > 20 ? 'Limited' : 'Almost Full';

  return (
    <div style={{ paddingTop: '64px', height: '100vh', display: 'flex', overflow: 'hidden' }}>
      {/* Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ x: -320 }}
            animate={{ x: 0 }}
            exit={{ x: -320 }}
            style={{
              width: '340px', minWidth: '340px', height: 'calc(100vh - 64px)',
              background: '#0f0f1a', borderRight: '1px solid rgba(99,102,241,0.2)',
              display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 10,
            }}
          >
            {/* Search & Filters */}
            <div style={{ padding: '16px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="🔍 Search parking..."
                style={{
                  width: '100%', padding: '10px 14px',
                  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '10px', color: '#fff', fontSize: '14px', outline: 'none',
                  marginBottom: '10px',
                }}
              />
              <div style={{ display: 'flex', gap: '8px' }}>
                <select
                  value={filter.vehicleType}
                  onChange={e => setFilter({ ...filter, vehicleType: e.target.value })}
                  style={{
                    flex: 1, padding: '8px', background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px',
                    color: '#fff', fontSize: '12px', outline: 'none',
                  }}
                >
                  <option value="">All Vehicles</option>
                  <option value="Car">Car</option>
                  <option value="Bike">Bike</option>
                  <option value="Bus">Bus</option>
                </select>
                <select
                  value={filter.maxPrice}
                  onChange={e => setFilter({ ...filter, maxPrice: e.target.value })}
                  style={{
                    flex: 1, padding: '8px', background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px',
                    color: '#fff', fontSize: '12px', outline: 'none',
                  }}
                >
                  <option value="">Any Price</option>
                  <option value="15">≤ ₹15/hr</option>
                  <option value="25">≤ ₹25/hr</option>
                  <option value="50">≤ ₹50/hr</option>
                </select>
              </div>
            </div>

            {/* Results count */}
            <div style={{ padding: '10px 16px', fontSize: '12px', color: '#64748b', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              {loading ? 'Loading...' : `${filteredParkings.length} parking spots found`}
            </div>

            {/* Parking List */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
              {filteredParkings.map((p) => (
                <motion.div
                  key={p._id || p.id}
                  whileHover={{ x: 2 }}
                  onClick={() => { setSelectedParking(p); setMapCenter([p.location.coordinates[1], p.location.coordinates[0]]); }}
                  style={{
                    padding: '14px', borderRadius: '12px', cursor: 'pointer', marginBottom: '6px',
                    background: selectedParking?._id === p._id ? 'rgba(99,102,241,0.15)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${selectedParking?._id === p._id ? 'rgba(99,102,241,0.4)' : 'rgba(255,255,255,0.06)'}`,
                    transition: 'all 0.2s',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: '700', flex: 1, marginRight: '8px' }}>{p.name}</h4>
                    <span style={{
                      fontSize: '11px', fontWeight: '700', color: statusColor(p),
                      background: `${statusColor(p)}20`, borderRadius: '6px', padding: '2px 8px', whiteSpace: 'nowrap',
                    }}>{statusText(p)}</span>
                  </div>
                  <p style={{ color: '#64748b', fontSize: '11px', marginBottom: '8px' }}>📍 {p.address}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#6366f1', fontWeight: '800', fontSize: '16px' }}>₹{p.pricePerHour}<span style={{ color: '#64748b', fontSize: '11px', fontWeight: '400' }}>/hr</span></span>
                    <span style={{ color: '#94a3b8', fontSize: '12px' }}>{p.availableSlots}/{p.totalSlots} free</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Map */}
      <div style={{ flex: 1, position: 'relative' }}>
        {/* Toggle Sidebar */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          style={{
            position: 'absolute', top: '16px', left: '16px', zIndex: 1000,
            background: '#1a1a2e', border: '1px solid rgba(99,102,241,0.3)',
            borderRadius: '10px', padding: '8px 14px', color: '#fff',
            fontSize: '13px', fontWeight: '600', cursor: 'pointer',
          }}
        >
          {sidebarOpen ? '◀ Hide' : '▶ Show List'}
        </button>

        {/* My Location Button */}
        {userLocation && (
          <button
            onClick={() => setMapCenter(userLocation)}
            style={{
              position: 'absolute', top: '16px', right: '16px', zIndex: 1000,
              background: '#1a1a2e', border: '1px solid rgba(99,102,241,0.3)',
              borderRadius: '10px', padding: '8px 14px', color: '#6366f1',
              fontSize: '13px', fontWeight: '600', cursor: 'pointer',
            }}
          >
            📍 My Location
          </button>
        )}

        <MapContainer
          center={mapCenter}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='© OpenStreetMap contributors'
          />
          <MapController center={mapCenter} />

          {/* User location */}
          {userLocation && (
            <>
              <Marker position={userLocation} icon={userIcon}>
                <Popup>📍 Your Location</Popup>
              </Marker>
              <Circle center={userLocation} radius={500} pathOptions={{ color: '#6366f1', fillColor: '#6366f1', fillOpacity: 0.05, weight: 1 }} />
            </>
          )}

          {/* Parking markers */}
          {filteredParkings.map((p) => {
            const coords = [p.location.coordinates[1], p.location.coordinates[0]];
            return (
              <Marker
                key={p._id || p.id}
                position={coords}
                icon={createParkingIcon(p.availableSlots, p.totalSlots)}
                eventHandlers={{ click: () => setSelectedParking(p) }}
              >
                <Popup>
                  <div style={{ minWidth: '200px', fontFamily: 'Inter, sans-serif' }}>
                    <strong style={{ fontSize: '14px' }}>{p.name}</strong>
                    <p style={{ color: '#666', fontSize: '12px', margin: '4px 0' }}>{p.address}</p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', margin: '8px 0' }}>
                      <span style={{ color: '#6366f1', fontWeight: '700' }}>₹{p.pricePerHour}/hr</span>
                      <span style={{ color: statusColor(p), fontWeight: '600', fontSize: '12px' }}>{p.availableSlots} slots free</span>
                    </div>
                    <button
                      onClick={() => navigate(`/parking/${p._id || p.id}`)}
                      style={{
                        width: '100%', padding: '8px', background: '#6366f1',
                        color: '#fff', border: 'none', borderRadius: '8px',
                        fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                      }}
                    >
                      View & Book →
                    </button>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>

        {/* Selected Parking Card */}
        <AnimatePresence>
          {selectedParking && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              style={{
                position: 'absolute', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
                background: '#1a1a2e', border: '1px solid rgba(99,102,241,0.3)',
                borderRadius: '16px', padding: '20px', width: '360px', zIndex: 1000,
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
              }}
            >
              <button
                onClick={() => setSelectedParking(null)}
                style={{ position: 'absolute', top: '12px', right: '12px', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '18px' }}
              >✕</button>
              <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '6px' }}>{selectedParking.name}</h3>
              <p style={{ color: '#64748b', fontSize: '12px', marginBottom: '12px' }}>📍 {selectedParking.address}</p>
              <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '22px', fontWeight: '800', color: '#6366f1' }}>₹{selectedParking.pricePerHour}</div>
                  <div style={{ fontSize: '11px', color: '#64748b' }}>per hour</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '22px', fontWeight: '800', color: statusColor(selectedParking) }}>{selectedParking.availableSlots}</div>
                  <div style={{ fontSize: '11px', color: '#64748b' }}>slots free</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '22px', fontWeight: '800', color: '#f59e0b' }}>★{selectedParking.rating?.toFixed(1)}</div>
                  <div style={{ fontSize: '11px', color: '#64748b' }}>rating</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => navigate(`/parking/${selectedParking._id || selectedParking.id}`)}
                  style={{
                    flex: 1, padding: '10px', background: 'rgba(99,102,241,0.1)',
                    border: '1px solid rgba(99,102,241,0.3)', borderRadius: '10px',
                    color: '#6366f1', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                  }}
                >
                  View Details
                </button>
                <button
                  onClick={() => navigate(`/book/${selectedParking._id || selectedParking.id}`)}
                  style={{
                    flex: 1, padding: '10px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    border: 'none', borderRadius: '10px',
                    color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer',
                  }}
                >
                  Book Now →
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}